import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { ProblemType, BankProblem, FeedbackType } from '../../types';
import type { FractionFocus } from '../common/FractionInput';
import { PROBLEM_DEFINITIONS, REQUIRED_WINS } from '../../constants';
import { NumberPad } from '../common/NumberPad';
import { FractionInput } from '../common/FractionInput';
import { ProgressDots } from './ProgressDots';
import { FeedbackOverlay } from './FeedbackOverlay';
import { HintFigure } from './HintFigure';
import { ScribbleCanvas } from './ScribbleCanvas';

interface GameScreenProps {
  selectedCategory: ProblemType;
  currentProblem: BankProblem;
  input: string;
  feedback: FeedbackType;
  showHint: boolean;
  sessionWins: number;
  showDecimal: boolean;
  hasDecimal: boolean;
  isFractionProblem: boolean;
  fractionNumerator: string;
  fractionDenominator: string;
  fractionFocus: FractionFocus;
  onExit: () => void;
  onNumber: (n: number) => void;
  onDecimal: () => void;
  onClear: () => void;
  onSubmit: () => void;
  onFractionFocusChange: (focus: FractionFocus) => void;
}

export const GameScreen: React.FC<GameScreenProps> = ({
  selectedCategory,
  currentProblem,
  input,
  feedback,
  showHint,
  sessionWins,
  showDecimal,
  hasDecimal,
  isFractionProblem,
  fractionNumerator,
  fractionDenominator,
  fractionFocus,
  onExit,
  onNumber,
  onDecimal,
  onClear,
  onSubmit,
  onFractionFocusChange,
}) => {
  const definition = PROBLEM_DEFINITIONS[selectedCategory];
  const required = REQUIRED_WINS[selectedCategory];

  // Clear scribbles when problem changes
  const [scribbleClearTrigger, setScribbleClearTrigger] = useState(0);
  useEffect(() => {
    setScribbleClearTrigger((prev) => prev + 1);
  }, [currentProblem]);

  return (
    <div className="h-[100dvh] overflow-hidden bg-[#f4fcf4] font-maru flex flex-col">
      {/* Fraction styles */}
      <style>{`
        .fraction-display {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 1rem 0;
        }
        .frac {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          vertical-align: middle;
        }
        .frac .num {
          border-bottom: 2px solid currentColor;
          padding: 0 0.5rem 0.25rem;
          font-size: 1.5rem;
          font-weight: bold;
        }
        .frac .denom {
          padding: 0.25rem 0.5rem 0;
          font-size: 1.5rem;
          font-weight: bold;
        }
      `}</style>

      {/* Header */}
      <div className="bg-white p-3 flex justify-between items-center shadow-sm z-10">
        <div
          className="px-4 py-1 rounded-full text-white font-bold text-sm flex items-center gap-2"
          style={{ backgroundColor: definition.color }}
        >
          {definition.title}
          <ProgressDots total={required} filled={sessionWins} />
        </div>
        <button
          onClick={onExit}
          className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft />
        </button>
      </div>

      {/* Question Area with Scribble Canvas */}
      <div className="flex-1 min-h-0 flex flex-col items-center p-4 overflow-y-auto relative">
        {/* Scribble Canvas Overlay */}
        <ScribbleCanvas clearTrigger={scribbleClearTrigger} />

        {/* questionHTML comes only from our own static bank (no user input),
            so dangerouslySetInnerHTML is safe here. */}
        <div
          className="text-xl font-bold text-center leading-relaxed text-gray-800 w-full max-w-2xl mb-6 relative z-0"
          dangerouslySetInnerHTML={{ __html: currentProblem.questionHTML }}
        />

        {/* Hint — figure (when relevant) + step-by-step reasoning */}
        {showHint && (
          <div className="w-full max-w-xl bg-white p-4 rounded-xl border-2 border-orange-200 shadow-lg animate-in fade-in slide-in-from-bottom-4 relative z-0">
            <div className="flex items-center gap-2 mb-2 bg-orange-50 px-3 py-1 rounded-lg w-fit">
              <span className="text-orange-500">💡</span>
              <span className="text-orange-700 font-bold text-sm">ヒント</span>
            </div>
            <HintFigure hint={currentProblem.hint} />
          </div>
        )}
      </div>

      {/* Input Display */}
      {isFractionProblem ? (
        <FractionInput
          numerator={fractionNumerator}
          denominator={fractionDenominator}
          focus={fractionFocus}
          onFocusChange={onFractionFocusChange}
        />
      ) : (
        <div className="flex justify-center px-4 pt-2">
          <div
            className={`text-5xl font-bold text-center w-full max-w-xs py-4 border-b-4 bg-white/60 rounded-t-xl transition-colors ${
              input ? 'text-gray-800 border-green-500' : 'text-transparent border-gray-300'
            }`}
          >
            {input || '_'}
          </div>
        </div>
      )}

      {/* Number Pad */}
      <div
        className="bg-white p-4 rounded-t-3xl shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-20"
        style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}
      >
        <NumberPad
          onNumber={onNumber}
          onDecimal={onDecimal}
          onClear={onClear}
          onSubmit={onSubmit}
          showDecimal={showDecimal && !isFractionProblem}
          hasDecimal={hasDecimal}
        />
      </div>

      {/* Feedback Overlay */}
      <FeedbackOverlay feedback={feedback} />
    </div>
  );
};
